import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Toolbar as MuiToolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getMyProfile, logout, qk } from 'api'
import { getCurrentAppVersion } from 'api/commom'
import { useAuthContext } from 'app/auth'
import { paths } from 'app/routes'
import { IconBurgerMenu, IconCross, IconLogo, IconLogOut } from 'assets/icons'
import { MyAvatar } from 'components/global'
import { Link } from 'elements/navigation'
import { useBoolean } from 'lib/hooks'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ChangeLanguageMenu } from './change-language-menu'
import { MenuDrawer } from './menu-drawer'
import { MyPlanBadge } from './my-plan-badge'
import { ProfileNavigationElements } from './profile-navigation-elements'
import { UpgradePlanMessage } from './upgrade-plan-message'

export const Toolbar = ({
  toolbarChildren,
  menuDrawerChildren,
  backLink,
}: {
  toolbarChildren?: React.ReactNode
  menuDrawerChildren?: (closeDrawer: () => void) => React.ReactNode
  backLink?: React.ReactNode
}) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { unauthorize } = useAuthContext()

  const theme = useTheme()

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const drawerOpen = useBoolean(false)

  const $logout = useMutation(logout)

  const $appVersion = useQuery(
    qk.common.appVersion.toKey(),
    getCurrentAppVersion,
  )

  const $profile = useQuery(qk.users.myProfile.toKey(), getMyProfile)

  if (!$profile.data) return null

  const { plan, status } = $profile.data

  const isTrialOrObservePlan =
    plan && (plan.name === 'trial' || plan.name === 'observe')

  const isConfirmedUser = status !== 'pending'

  return (
    <Box>
      <AppBar
        position="static"
        sx={{
          border: 'none',
        }}
      >
        {isTrialOrObservePlan && isConfirmedUser && <UpgradePlanMessage />}

        <MuiToolbar
          sx={{
            minHeight: { xs: 'auto' },
            zIndex: theme => theme.zIndex.drawer + 1,
          }}
        >
          <Box
            height="100%"
            display="flex"
            justifyContent="space-between"
            width="100%"
            px={5}
            py={1}
            borderBottom="2px solid"
            borderColor="primary.light"
          >
            <Box display="flex" alignItems="center">
              <Link to="/" display="flex" alignItems="end" underline="none">
                <IconLogo sx={{ fontSize: '2rem' }} />

                {$appVersion.data && (
                  <Typography
                    color="text.secondary"
                    variant="caption2"
                    lineHeight="100%"
                  >
                    {$appVersion.data.latestRelease}
                  </Typography>
                )}
              </Link>

              {backLink}
            </Box>

            {(isDesktop || !menuDrawerChildren) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  transform: 'translateX(-50%)',
                  height: '100%',
                }}
              >
                {toolbarChildren}
              </Box>
            )}

            <Box display="flex" alignItems="center">
              <MyPlanBadge />
              <ChangeLanguageMenu />
              {(!isMobile || !menuDrawerChildren) && (
                <ProfileNavigationElements />
              )}
              {!isDesktop && menuDrawerChildren && (
                <IconButton
                  sx={{ ml: 3 }}
                  onClick={() => {
                    drawerOpen.toggle()
                  }}
                >
                  {drawerOpen.isTrue ? <IconCross /> : <IconBurgerMenu />}
                </IconButton>
              )}
            </Box>
          </Box>
        </MuiToolbar>
      </AppBar>

      {!isDesktop && menuDrawerChildren && (
        <MenuDrawer isOpen={drawerOpen.isTrue} onClose={drawerOpen.setFalse}>
          <Stack height="100%">
            {isMobile && (
              <>
                <Link
                  to={paths.profileSettings}
                  underline="none"
                  sx={{ color: 'text.primary' }}
                >
                  <Box display="flex" alignItems="center">
                    <MyAvatar size={40} />

                    <Typography variant="caption1" ml={1.5}>
                      {t('user_profile.profile_settings')}
                    </Typography>
                  </Box>
                </Link>

                <Divider sx={{ my: 2 }} />
              </>
            )}

            {menuDrawerChildren?.(drawerOpen.setFalse)}

            {isMobile && (
              <Box display="flex" mt="auto">
                <Button
                  sx={{
                    color: 'error.main',
                    px: 0.5,

                    '&:hover': { background: 'none' },
                  }}
                  startIcon={<IconLogOut />}
                  onClick={() => {
                    $logout.mutate(undefined, {
                      onSuccess: () => {
                        unauthorize()
                        queryClient.removeQueries()
                        navigate(paths.signIn)
                      },
                    })
                  }}
                >
                  {t('common.log_out')}
                </Button>
              </Box>
            )}
          </Stack>
        </MenuDrawer>
      )}
    </Box>
  )
}
